import { useState, useEffect, useMemo } from "react";
import { PrimaryButton, Label, ComboBox, Checkbox, IComboBoxOption } from "@fluentui/react";
import { loadMetricsDataApi, updateMetricsDataApi, fetchUserDataApi } from "../../api";
import { Line } from "react-chartjs-2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, ChartOptions } from "chart.js";
import styles from "./Report.module.css";
import "chart.js/auto";
import i18next from "../../i18n/i18n";
import Fuse from "fuse.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

interface SentimentCounts {
    Empty: number;
    Positive: number;
    Negative: number;
}

interface MetricDetails {
    faq: SentimentCounts;
    manuals: SentimentCounts;
    Sessions: number;
}

interface MonthlyMetrics {
    faq: number;
    manuals: number;
    details: MetricDetails;
}

interface ComboOptions extends IComboBoxOption {
    key: string;
    text: string;
}

type MonthMap = Record<string, MonthlyMetrics>;
type ProductMap = Record<string, MonthMap>;
type UserMap = Record<string, ProductMap>;
type MetricsData = Record<string, UserMap>;

interface CorpOption extends IComboBoxOption {
    key: string;
    text: string;
}

type CorpMetrics = Record<string, MonthlyMetrics>;
type MetricsToView = Record<string, CorpMetrics>;

const Report = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [showProducts, setShowProducts] = useState(true);
    const [masterData, setMasterData] = useState<MetricsData>({});
    const [metricsData, setMetricsData] = useState<any>({});
    const [selectedCorpIds, setSelectedCorpIds] = useState<string[]>(["all"]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>(["all"]);
    const [coprListEntries, setCoprListEntries] = useState<ComboOptions[]>([]);
    const [corpListOptions, setCorpListOptions] = useState<ComboOptions[]>([]);
    const [userListEntries, setUserListEntries] = useState<ComboOptions[]>([]);
    const [userListOptions, setUserListOptions] = useState<ComboOptions[]>([]);
    const [relatedProducts, setRelatedProducts] = useState<string[]>([]);
    const [productCheckboxes, setProductCheckboxes] = useState<string[]>([]);
    const flattenedMetrics = useMemo(() => flattenMetrics(), [selectedCorpIds, selectedUserIds, productCheckboxes]);

    useEffect(() => {
        loadMetricsData();
    }, []);

    const loadMetricsData = async () => {
        try {
            setIsLoading(true);
            const response = await loadMetricsDataApi();
            const result = await response.json();
            if (!result || Object.keys(result).length === 0) return;
            const metricKeys = Object.keys(result).filter(corpId => corpId !== "common_info");
            const corps = [{ key: "all", text: i18next.t("report.allOption") }, ...metricKeys.map(corpId => ({ key: corpId, text: corpId }))];
            const selected = ["all", ...metricKeys];
            setMasterData(result);
            setCoprListEntries(corps);
            setCorpListOptions(corps);
            setSelectedCorpIds(selected);
            updateProductList(result, selected);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateProductList = (data: any, selectedCorpIds: string[]) => {
        const mergedProducts = new Set<string>();
        const getProducts = (corpId: string): string[] => (Array.isArray(data[corpId]?.product_list) ? data[corpId].product_list : []);
        if (selectedCorpIds.includes("all")) {
            getProducts("common_info").forEach(product => mergedProducts.add(product));
        } else {
            selectedCorpIds.forEach(corpId => {
                getProducts(corpId).forEach(product => mergedProducts.add(product));
            });
        }
        const mergedArray = Array.from(mergedProducts);
        setRelatedProducts(mergedArray);
        setProductCheckboxes(mergedArray);
    };

    const updateUserList = (selectedCorpIds: string[]) => {
        const users: { key: string; text: string }[] = [{ key: "all", text: i18next.t("report.allCustomers") }];
        const getUserList = (corpId: string) => (Array.isArray(masterData?.[corpId]?.user_list) ? masterData[corpId].user_list : []);
        const corpIdsToProcess = selectedCorpIds.includes("all") ? ["common_info"] : selectedCorpIds;
        corpIdsToProcess.forEach(corpId => {
            users.push(...getUserList(corpId));
        });
        const mergedUserList = users.map(user => user.key);
        setSelectedUserIds(["all", ...mergedUserList]);
        setUserListEntries(users);
        setUserListOptions(users);
    };

    const downloadReport = async () => {
        setIsLoading(true);
        try {
            const response = await fetchUserDataApi(selectedCorpIds);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const now = new Date();
            const formatted = now.toISOString().replace(/[-:T]/g, "").slice(0, 14);
            const filename = `user_data_list_${formatted}.ods`;
            const link = Object.assign(document.createElement("a"), {
                href: url,
                download: filename
            });
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Error downloading report:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const prepareChartData = (sourceData: MetricsToView | undefined, selectedCorpId: string) => {
        if (!sourceData) return null;
        const faqData: number[] = [];
        const manualsData: number[] = [];
        const faqMeta: any[] = [];
        const manualsMeta: any[] = [];
        const allMonthsSet = new Set<string>();
        const corpData = sourceData[selectedCorpId];
        if (!corpData) return null;

        Object.keys(corpData).forEach(month => allMonthsSet.add(month));
        const allMonths = Array.from(allMonthsSet).sort();
        allMonths.forEach(month => {
            const record = corpData?.[month];
            faqData.push(record?.faq ?? null);
            manualsData.push(record?.manuals ?? null);
            faqMeta.push(record?.details?.faq || {});
            manualsMeta.push(record?.details?.manuals || {});
        });

        return {
            labels: allMonths,
            datasets: [
                {
                    label: i18next.t("report.faqConversations"),
                    data: faqData,
                    borderColor: "#147ccb",
                    backgroundColor: "#147ccb",
                    fill: false,
                    tension: 0.3,
                    metaData: faqMeta
                },
                {
                    label: i18next.t("report.manualsConversations"),
                    data: manualsData,
                    borderColor: "#0fbf2a",
                    backgroundColor: "#0fbf2a",
                    fill: false,
                    tension: 0.3,
                    metaData: manualsMeta
                }
            ]
        };
    };

    const chartOptions: ChartOptions<"line"> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top" as const
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const month = context.label as string;
                        const datasetLabel = context.dataset.label || "";
                        const value = context.parsed.y;
                        const chartContainer: HTMLElement | null = context.chart.canvas.closest(`.${styles.chart_card}`);
                        const corpId = chartContainer?.querySelector("span")?.textContent ?? "unknown";
                        const corpData = metricsData[corpId]?.[month];
                        const type = datasetLabel.toLowerCase().includes("faq") ? "faq" : "manuals";
                        const lines = [`${datasetLabel}: ${value}`];
                        lines.push(` ${i18next.t("report.Sessions")} : ${corpData?.details?.Sessions ?? 0}`);
                        if (corpData?.details?.[type]) {
                            const details = corpData.details[type];
                            const detailsLines = Object.entries(details).map(([k, v]) => `  ${i18next.t(`report.${k}`)}: ${v}`);
                            lines.push(...detailsLines);
                        }

                        return lines;
                    }
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: i18next.t("report.monthRange")
                }
            },
            y: {
                ticks: {
                    callback: function (value) {
                        if (Number.isInteger(value)) {
                            return value;
                        }
                    }
                },
                beginAtZero: true,
                title: {
                    display: true,
                    text: i18next.t("report.noOfConversations")
                }
            }
        }
    };

    function flattenMetrics() {
        try {
            const aggregatedMetrics: MetricsToView = {};
            for (const [corpId, userMap] of Object.entries(masterData)) {
                if (corpId === "common_info") continue;
                if (!selectedCorpIds?.includes(corpId)) continue;
                if (!aggregatedMetrics[corpId]) aggregatedMetrics[corpId] = {};
                for (const [userId, productMap] of Object.entries(userMap["data"])) {
                    if (!selectedCorpIds?.includes("all") && !selectedUserIds?.includes(userId)) continue;
                    for (const [product_name, monthMap] of Object.entries(productMap)) {
                        if (!productCheckboxes?.includes(product_name)) continue;
                        for (const [month, entry] of Object.entries(monthMap)) {
                            if (!aggregatedMetrics[corpId][month]) {
                                aggregatedMetrics[corpId][month] = {
                                    faq: 0,
                                    manuals: 0,
                                    details: {
                                        faq: { Empty: 0, Positive: 0, Negative: 0 },
                                        manuals: { Empty: 0, Positive: 0, Negative: 0 },
                                        Sessions: 0
                                    }
                                };
                            }
                            const target = aggregatedMetrics[corpId][month];
                            target.faq += entry.faq;
                            target.manuals += entry.manuals;
                            target.details.faq.Empty += entry.details.faq.Empty;
                            target.details.faq.Positive += entry.details.faq.Positive;
                            target.details.faq.Negative += entry.details.faq.Negative;
                            target.details.manuals.Empty += entry.details.manuals.Empty;
                            target.details.manuals.Positive += entry.details.manuals.Positive;
                            target.details.manuals.Negative += entry.details.manuals.Negative;
                            target.details.Sessions += entry.details.Sessions;
                        }
                    }
                }
            }
            setMetricsData(aggregatedMetrics);
            return aggregatedMetrics;
        } catch (error) {
            console.error("Error flattening metrics", error);
        }
    }

    const Spinner = () => {
        return (
            <div className={styles.full_page_spinner}>
                <div className={styles.spinner}></div>
            </div>
        );
    };

    const handleSearch = (initialData: any, value: string, setState: any) => {
        if (value.length === 0) {
            setState(initialData);
            return;
        }
        const options = {
            includeScore: true,
            includeMatches: true,
            ignoreLocation: true,
            threshold: 0.6,
            keys: ["text"]
        };
        const fuse = new Fuse(initialData, options);
        const results = fuse.search(value);
        const items = results.map(result => result.item);
        setState(items);
    };

    const handleComboChange = (item: any) => {
        if (!item) return;
        const isSelecting = item.selected;
        const itemKey = item.key as string;

        let updatedCorpIds = selectedCorpIds;
        if (itemKey === "all") {
            updatedCorpIds = isSelecting ? corpListOptions.map(opt => opt.key.toString()) : [];
        } else {
            updatedCorpIds = isSelecting ? [...selectedCorpIds, itemKey] : selectedCorpIds.filter(id => id !== itemKey);

            if (!isSelecting && selectedCorpIds.includes("all")) {
                updatedCorpIds = updatedCorpIds.filter(id => id !== "all");
            }

            const allRealCorpKeys = corpListOptions.map(opt => opt.key.toString()).filter(k => k !== "all");

            if (allRealCorpKeys.every(k => updatedCorpIds.includes(k)) && !updatedCorpIds.includes("all")) {
                updatedCorpIds = [...updatedCorpIds, "all"];
            }
        }
        setSelectedCorpIds(updatedCorpIds);
        updateProductList(masterData, updatedCorpIds);
        updateUserList(updatedCorpIds);
    };

    const handleUserComboChange = (item: any) => {
        if (!item) return;
        const isSelecting = item.selected;
        const itemKey = item.key as string;
        let updated = isSelecting ? [...selectedUserIds, itemKey] : selectedUserIds.filter(id => id !== itemKey);
        if (itemKey === "all") {
            updated = isSelecting ? userListOptions.map(u => u.key.toString()) : [];
        } else {
            if (!isSelecting && selectedUserIds.includes("all")) {
                updated = updated.filter(id => id !== "all");
            }
            const allRealUserKeys = userListOptions.map(opt => opt.key.toString()).filter(k => k !== "all");
            if (allRealUserKeys.every(k => updated.includes(k)) && !updated.includes("all")) {
                updated = [...updated, "all"];
            }
        }
        setSelectedUserIds(updated);
    };

    return (
        <div className={styles.report_container}>
            {isLoading && <Spinner />}
            <div className={styles.sticky_controls}>
                <div className={styles.dropdown_container}>
                    <Label>{i18next.t("report.customerCorpIds")}</Label>
                    <ComboBox
                        multiSelect
                        className="corpIdList"
                        style={{ minWidth: "140px", margin: "2px", borderRadius: "5px" }}
                        selectedKey={selectedCorpIds}
                        placeholder={i18next.t("filters.corpId")}
                        allowFreeform
                        autoComplete="on"
                        options={corpListOptions}
                        onChange={(event, item) => {
                            handleComboChange(item);
                        }}
                        onInputValueChange={text => handleSearch(coprListEntries, text, setCorpListOptions)}
                    />
                    {!selectedCorpIds?.includes("all") && selectedCorpIds?.length > 0 && (
                        <div className={styles.dropdown_container}>
                            <Label>{i18next.t("report.userList")}</Label>
                            <ComboBox
                                multiSelect
                                className="userList"
                                style={{ minWidth: "140px", margin: "2px", borderRadius: "5px" }}
                                selectedKey={selectedUserIds}
                                placeholder={i18next.t("filters.userId")}
                                allowFreeform
                                autoComplete="on"
                                options={userListOptions}
                                onChange={(event, item) => {
                                    handleUserComboChange(item);
                                }}
                                onInputValueChange={text => handleSearch(userListEntries, text, setUserListOptions)}
                            />
                        </div>
                    )}
                </div>

                <div className={styles.button_container}>
                    <PrimaryButton
                        className={styles.button_style}
                        text={i18next.t("report.syncData")}
                        onClick={async () => {
                            setIsLoading(true);
                            await updateMetricsDataApi();
                            await loadMetricsData();
                        }}
                        disabled={isLoading}
                        title={i18next.t("report.SyncButtonInfo")}
                    />
                    <PrimaryButton
                        className={styles.button_style}
                        text={i18next.t("report.downloadData")}
                        onClick={async () => {
                            await downloadReport();
                        }}
                        disabled={isLoading}
                        title={i18next.t("report.downloadButtonInfo")}
                    />
                </div>
            </div>
            <div className={styles.chart_scroll_container}>
                <div className={styles.grid_container}>
                    {selectedCorpIds.map(corpName => {
                        const chartData = prepareChartData(flattenedMetrics, corpName);
                        if (!chartData) return null;
                        return (
                            <div key={corpName} className={styles.chart_card}>
                                <span>{corpName}</span>
                                <Line data={chartData} options={chartOptions} />
                            </div>
                        );
                    })}
                </div>
            </div>
            {selectedCorpIds?.length === 0 && !isLoading && (
                <div className={styles.empty_message}>
                    <Label style={{ fontSize: "xxx-large" }}>{i18next.t("report.nodata")}</Label>
                </div>
            )}
            {relatedProducts.length > 0 && (
                <div className={styles.checkbox_container}>
                    <div className={styles.product_header} onClick={() => setShowProducts(prev => !prev)}>
                        <span className={styles.product_title}>{i18next.t("report.productList")}</span>
                        <div className={styles.icon_toggle} title={showProducts ? i18next.t("report.hideProducts") : i18next.t("report.showProducts")}>
                            {showProducts ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                        </div>
                    </div>
                    {showProducts &&
                        relatedProducts.map(product => (
                            <Checkbox
                                key={product}
                                label={product}
                                checked={productCheckboxes.includes(product)}
                                onChange={(_, checked) => {
                                    setProductCheckboxes(prev => (checked ? [...prev, product] : prev.filter(p => p !== product)));
                                }}
                            />
                        ))}
                </div>
            )}
        </div>
    );
};

export default Report;
